import { useMutation } from "react-query";
import { TCreateBankAccountRequest } from "../../api/bankAccount/apiCreateBankAccount";
import { apiCreateContact } from "../../api/contact/apiCreateContact";
import { apiCreateContactServiceHolderRelationship } from "../../api/contactServiceHolderRelationship/apiCreateContactServiceHolderRelationship";
import { apiDeleteContactServiceHolderRelationship } from "../../api/contactServiceHolderRelationship/apiDeleteContactServiceHolderRelationship";
import { apiUpdateContactServiceHolderRelationship } from "../../api/contactServiceHolderRelationship/apiUpdateContactServiceHolderRelationship";
import { TUpdateServiceRequest } from "../../api/service/apiUpdateService";
import { TCreateServiceHolderRequest } from "../../api/serviceHolder/apiCreateServiceHolder";
import { TUpdateServiceHolderRequest } from "../../api/serviceHolder/apiUpdateServiceHolder";
import {
  TServiceValuationTaskError,
  TValuationFormTask,
} from "../../apps/affinity/pages/ServicePage/components/ServiceValuation/types";
import { queryClient } from "../../lib/react-query";

export default function useSaveAndValidateMutation(
  apiCreateServiceHolder: (
    serviceHolderId: string,
    serviceHolder: TCreateServiceHolderRequest
  ) => Promise<unknown>,
  apiUpdateServiceHolder: (
    serviceHolderId: string,
    serviceHolder: TUpdateServiceHolderRequest
  ) => Promise<unknown>,
  apiCreateBankAccount: (
    bankAccountId: string,
    bankAccount: TCreateBankAccountRequest
  ) => Promise<unknown>,
  apiUpdateService: (
    serviceId: string,
    service: TUpdateServiceRequest
  ) => Promise<unknown>
) {
  const saveAndValidate = async ({
    serviceId,
    serviceHolderId,
    tasks,
    onValuationProcessUpdate,
    onMutationComplete,
  }: {
    serviceId: string;
    serviceHolderId: string;
    tasks: TValuationFormTask[];
    onValuationProcessUpdate: (
      taskId: number,
      status: "success" | "error" | "pending",
      errors: TServiceValuationTaskError[]
    ) => void;
    onMutationComplete: () => void;
  }) => {
    const serviceHolderTask = tasks.find(
      (task) =>
        task.name === "create_service_holder" ||
        task.name === "update_service_holder"
    );

    if (serviceHolderTask) {
      if (serviceHolderTask.name === "create_service_holder") {
        try {
          await apiCreateServiceHolder(
            serviceHolderId,
            serviceHolderTask.serviceHolder
          );
        } catch (e) {
          onValuationProcessUpdate(serviceHolderTask.id, "error", [
            "error_creating_service_holder",
          ]);
          return;
        }
      } else if (serviceHolderTask.name === "update_service_holder") {
        try {
          await apiUpdateServiceHolder(
            serviceHolderId,
            serviceHolderTask.serviceHolder
          );
        } catch (e) {
          onValuationProcessUpdate(serviceHolderTask.id, "error", [
            "error_updating_service_holder",
          ]);
          return;
        }
      }

      onValuationProcessUpdate(serviceHolderTask.id, "success", []);
    }

    const apiTasks: Promise<unknown>[] = [];
    tasks.forEach((task) => {
      if (task.name === "update_service") {
        apiTasks.push(
          apiUpdateService(serviceId, task.service)
            .then(() => {
              onValuationProcessUpdate(task.id, "success", []);
            })
            .catch((reason) => {
              onValuationProcessUpdate(task.id, "error", [
                "error_updating_service",
              ]);
            })
        );
      }

      if (task.name === "new_relation_existing_contact") {
        apiTasks.push(
          apiCreateContactServiceHolderRelationship(task.relationship)
            .then(() => {
              onValuationProcessUpdate(task.id, "success", []);
            })
            .catch((reason) => {
              onValuationProcessUpdate(task.id, "error", [
                "error_creating_new_relationship",
              ]);
            })
        );
      }

      if (task.name === "new_relation_new_contact") {
        apiTasks.push(
          apiCreateContact(task.contact)
            .then(() => {
              apiCreateContactServiceHolderRelationship(task.relationship)
                .then(() => {
                  onValuationProcessUpdate(task.id, "success", []);
                })
                .catch((reason) => {
                  onValuationProcessUpdate(task.id, "error", [
                    "error_creating_new_relationship",
                  ]);
                });
            })
            .catch((reason) => {
              onValuationProcessUpdate(task.id, "error", [
                "error_creating_new_contact",
              ]);
            })
        );
      }

      if (task.name === "existing_relation_remove") {
        apiTasks.push(
          apiDeleteContactServiceHolderRelationship(task.relationshipId)
            .then(() => {
              onValuationProcessUpdate(task.id, "success", []);
            })
            .catch((reason) => {
              onValuationProcessUpdate(task.id, "error", [
                "error_removing_existing_relation",
              ]);
            })
        );
      }

      if (task.name === "modify_existing_relation") {
        apiTasks.push(
          apiUpdateContactServiceHolderRelationship(
            task.relationshipId,
            task.relationship
          )
            .then(() => {
              onValuationProcessUpdate(task.id, "success", []);
            })
            .catch((reason) => {
              onValuationProcessUpdate(task.id, "error", [
                "error_modifying_existing_relation",
              ]);
            })
        );
      }
    });
    await Promise.all(apiTasks).then(() => {});

    onMutationComplete();
  };

  return useMutation(saveAndValidate, {
    onMutate: ({ serviceId }) => {
      queryClient.cancelQueries(["service", serviceId]);
      queryClient.cancelQueries(["services"]);
    },
    onError: (err, data, rollback) => {},
    onSettled: (data, error, { serviceId }) => {
      queryClient.invalidateQueries(["service", serviceId]);
      queryClient.invalidateQueries(["services"]);

      // Invalidate queries for services sidenav button
      queryClient.invalidateQueries(["my_unvalued_services"]);
    },
  });
}
